import React, {useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {CaretDown, Check, Desktop, Moon, Sun} from '@phosphor-icons/react';
import {useTheme, type ThemePreference} from './theme';

const choices = [
  {value: 'system', label: 'System', Icon: Desktop},
  {value: 'light', label: 'Light', Icon: Sun},
  {value: 'dark', label: 'Dark', Icon: Moon},
] as const;

export function ThemeMenu() {
  const {preference, setPreference} = useTheme();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState({left: 0, top: 0});
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const id = useId();
  const selected = choices.findIndex(choice => choice.value === preference);
  const CurrentIcon = choices[selected].Icon;
  function show(index = selected) { setActive(index); setOpen(true); }
  function close(restoreFocus = false) {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  }
  function choose(value: ThemePreference) { setPreference(value); close(true); }
  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      if (!trigger.current || !menu.current) return;
      const rect = trigger.current.getBoundingClientRect();
      const box = menu.current.getBoundingClientRect();
      const top = rect.bottom + box.height + 8 <= innerHeight
        ? rect.bottom + 6 : Math.max(8, rect.top - box.height - 6);
      setPosition({left: Math.max(8, Math.min(rect.left, innerWidth - box.width - 8)), top});
    }
    function outside(event: PointerEvent) {
      if (!trigger.current?.contains(event.target as Node) && !menu.current?.contains(event.target as Node)) close();
    }
    place();
    items.current[active]?.focus();
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  function navigate(event: React.KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (event.key === 'Tab') { close(true); return; }
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1
      : event.key === 'ArrowDown' ? (active + 1) % choices.length
      : event.key === 'ArrowUp' ? (active + choices.length - 1) % choices.length : null;
    if (next !== null) { event.preventDefault(); setActive(next); items.current[next]?.focus(); }
  }
  return <div className="ui-theme-select">
    <button ref={trigger} type="button" className="ui-theme-trigger" aria-label="Appearance"
      aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => open ? close(true) : show()}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); show(event.key === 'ArrowDown' ? 0 : choices.length - 1);
        }
      }}>
      <CurrentIcon size={16} aria-hidden="true"/><span>{choices[selected].label}</span><CaretDown size={14} aria-hidden="true"/>
    </button>
    {open && createPortal(<div ref={menu} id={id} role="menu" aria-label="Appearance options"
      className="ui-theme-menu" style={position} onKeyDown={navigate}
      onBlur={event => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node) && event.relatedTarget !== trigger.current) close(); }}>
      {choices.map(({value, label, Icon}, index) => <button key={value} type="button"
        ref={element => { items.current[index] = element; }} role="menuitemradio"
        aria-checked={preference === value} tabIndex={active === index ? 0 : -1}
        className="ui-theme-option" onFocus={() => setActive(index)} onClick={() => choose(value)}>
        <Icon size={17} aria-hidden="true"/><span>{label}</span><Check size={16} aria-hidden="true" className={preference === value ? '' : 'ui-theme-unchecked'}/>
      </button>)}
    </div>, document.body)}
  </div>;
}
